/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Recommendation Engine — Part 1
 * ============================================================
 *
 * Requires:
 * 1. rise-skin-products.js
 * 2. rise-skin-quiz.js
 *
 * This file:
 * - Normalizes quiz answers
 * - Builds concern / phenotype scores
 * - Applies safety gates
 * - Scores eligible products
 * - Returns ranked product candidates
 *
 * Later parts will:
 * - Assemble Essential / Complete / Elevated routines
 * - Prevent redundancy
 * - Manage active load
 * - Match treatments
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};


// ============================================================
// ENGINE CONFIG
// ============================================================

window.RISE_SKIN.ENGINE_VERSION = "1.0";

window.RISE_SKIN.engineConfig = {
  primaryConcernBonus: 3,
  secondaryConcernBonus: 2,
  tertiaryConcernBonus: 1,

  minimumProductScore: 1,

  fitWeight: 1,
  concernWeight: 1,

  blockedScore: -9999
};


// ============================================================
// SAFE ARRAY HELPER
// ============================================================

window.RISE_SKIN.asArray = function (value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return [];
  }

  return [value];
};


// ============================================================
// NORMALIZE CLIENT PROFILE
// ============================================================

window.RISE_SKIN.normalizeProfileForEngine =
  function (profile) {

    const source =
      profile ||
      (
        window.RISE_SKIN.quizState
          ? window.RISE_SKIN.quizState.profile
          : {}
      ) ||
      {};

    return {
      concerns:
        window.RISE_SKIN.asArray(
          source.concerns
        ),

      ageRange:
        source.ageRange || null,

      skinBehavior:
        source.skinBehavior || null,

      sensitivity:
        source.sensitivity || null,

      barrierStatus:
        source.barrierStatus || null,

      acneSeverity:
        source.acneSeverity || null,

      pigmentType:
        source.pigmentType || null,

      rednessPattern:
        source.rednessPattern || null,

      agingConcerns:
        window.RISE_SKIN.asArray(
          source.agingConcerns
        ),

      eyeConcerns:
        window.RISE_SKIN.asArray(
          source.eyeConcerns
        ),

        bodyConcerns:
  window.RISE_SKIN.asArray(
    source.bodyConcerns
  ),

  sunExposure:
  source.sunExposure || null,

      spfConsistency:
        source.spfConsistency || null,

      spfPreference:
        window.RISE_SKIN.asArray(
          source.spfPreference
        ),

      routineCommitment:
        source.routineCommitment || "complete",

      pregnant:
        Boolean(source.pregnant),

      breastfeeding:
        Boolean(source.breastfeeding),

      prescriptionRetinoid:
        Boolean(source.prescriptionRetinoid),

      recentProcedure:
        Boolean(source.recentProcedure),

      treatmentInterest:
        source.treatmentInterest || null,

      treatmentFit:
        source.treatmentFit || null,

      eventTiming:
        source.eventTiming || null
    };
  };


// ============================================================
// SCORE BUCKETS
// ============================================================

window.RISE_SKIN.createScoreBuckets =
  function () {

    return {
      acne: 0,
      pores: 0,
      texture: 0,
      pigment: 0,
      redness: 0,
      fineLines: 0,
      laxity: 0,
      dullness: 0,
      dehydration: 0,
      congestion: 0,
      underEye: 0,

      dryness: 0,
      oiliness: 0,
      sensitivity: 0,
      resilience: 0,

      dynamicLines: 0,
      volumeLoss: 0,
      vascular: 0,
      melasmaPattern: 0,

      uvExposure: 0,
      spfAdherence: 0
    };
  };


// ============================================================
// MAP PRIMARY CONCERNS
// ============================================================

window.RISE_SKIN.applyPrimaryConcernScores =
  function (scores, concerns) {

    const concernMap = {
      acne: ["acne"],
      pigment: ["pigment"],
      redness: ["redness"],
      fine_lines: ["fineLines"],
      laxity: ["laxity"],
      texture: ["texture"],
      pores: ["pores"],
      dullness: ["dullness"],
      dehydration: ["dehydration"],
      under_eye: ["underEye"],
      prevention: ["fineLines"]
    };

    concerns.forEach(
      (concern, index) => {

        const bonus =
          index === 0
            ? window.RISE_SKIN
                .engineConfig
                .primaryConcernBonus
            : index === 1
              ? window.RISE_SKIN
                  .engineConfig
                  .secondaryConcernBonus
              : window.RISE_SKIN
                  .engineConfig
                  .tertiaryConcernBonus;

        const targets =
          concernMap[concern] || [];

        targets.forEach(
          (key) => {
            scores[key] +=
              5 + bonus;
          }
        );
      }
    );
  };


// ============================================================
// SKIN BEHAVIOR
// ============================================================

window.RISE_SKIN.applySkinBehaviorScores =
  function (scores, profile) {

    switch (profile.skinBehavior) {

      case "dry":
        scores.dryness += 5;
        scores.dehydration += 4;
        break;

      case "balanced":
        break;

      case "combination":
        scores.oiliness += 3;
        break;

      case "oily":
        scores.oiliness += 5;
        scores.congestion += 2;
        break;

      case "variable":
        scores.dryness += 1;
        scores.oiliness += 1;
        break;
    }


    switch (profile.sensitivity) {

      case "resilient":
        scores.resilience += 5;
        break;

      case "normal":
        scores.resilience += 3;
        break;

      case "sensitive":
        scores.sensitivity += 4;
        break;

      case "very_sensitive":
        scores.sensitivity += 5;
        break;
    }
  };


// ============================================================
// ACNE
// ============================================================

window.RISE_SKIN.applyAcneScores =
  function (scores, profile) {

    switch (profile.acneSeverity) {

      case "occasional":
        scores.acne += 2;
        break;

      case "congested":
        scores.acne += 4;
        scores.congestion += 5;
        scores.pores += 2;
        break;

      case "inflamed":
        scores.acne += 5;
        break;

      case "cystic":
        scores.acne += 6;
        break;
    }
  };


// ============================================================
// PIGMENT
// ============================================================

window.RISE_SKIN.applyPigmentScores =
  function (scores, profile) {

    switch (profile.pigmentType) {

      case "light_sun":
        scores.pigment += 2;
        break;

      case "sun_damage":
        scores.pigment += 5;
        break;

      case "post_acne":
        scores.pigment += 4;
        scores.acne += 1;
        break;

      case "melasma_pattern":
        scores.pigment += 5;
        scores.melasmaPattern += 5;
        break;
    }
  };


// ============================================================
// REDNESS
// ============================================================

window.RISE_SKIN.applyRednessScores =
  function (scores, profile) {

    switch (profile.rednessPattern) {

      case "flushing":
        scores.redness += 2;
        break;

      case "persistent":
        scores.redness += 5;
        break;

      case "vascular":
        scores.redness += 4;
        scores.vascular += 5;
        break;

      case "reactive":
        scores.redness += 5;
        scores.sensitivity += 3;
        break;
    }
  };


// ============================================================
// AGING / STRUCTURAL
// ============================================================

window.RISE_SKIN.applyAgingScores =
  function (scores, profile) {

    profile.agingConcerns.forEach(
      (concern) => {

        switch (concern) {

          case "prevention":
            scores.fineLines += 1;
            break;

          case "early_lines":
            scores.fineLines += 3;
            break;

          case "dynamic_lines":
            scores.dynamicLines += 5;
            scores.fineLines += 1;
            break;

          case "resting_lines":
            scores.fineLines += 5;
            break;

          case "laxity":
            scores.laxity += 5;
            break;

          case "volume_loss":
            scores.volumeLoss += 5;
            break;
        }
      }
    );
  };


// ============================================================
// UNDER-EYE
// ============================================================

window.RISE_SKIN.applyEyeScores =
  function (scores, profile) {

    profile.eyeConcerns.forEach(
      (concern) => {

        switch (concern) {

          case "prevention":
            scores.underEye += 1;
            break;

          case "fine_lines":
            scores.underEye += 5;
            scores.fineLines += 1;
            break;

          case "puffiness":
            scores.underEye += 4;
            break;

          case "dark_circles":
            scores.underEye += 4;
            break;

          case "hollowing":
            scores.underEye += 3;
            scores.volumeLoss += 5;
            break;
        }
      }
    );
  };


// ============================================================
// SUN / SPF
// ============================================================

window.RISE_SKIN.applySunScores =
  function (scores, profile) {

    switch (profile.sunExposure) {

      case "low":
        scores.uvExposure += 1;
        break;

      case "moderate":
        scores.uvExposure += 2;
        break;

      case "high":
        scores.uvExposure += 4;
        break;

      case "very_high":
        scores.uvExposure += 5;
        break;
    }


    switch (profile.spfConsistency) {

      case "daily":
        scores.spfAdherence += 5;
        break;

      case "most_days":
        scores.spfAdherence += 3;
        break;

      case "outdoor_only":
        scores.spfAdherence += 1;
        break;

      case "rarely":
        scores.spfAdherence -= 5;
        break;
    }
  };


// ============================================================
// BUILD CLIENT SCORES
// ============================================================

window.RISE_SKIN.buildClientScores =
  function (profileInput) {

    const profile =
      window.RISE_SKIN
        .normalizeProfileForEngine(
          profileInput
        );

    const scores =
      window.RISE_SKIN
        .createScoreBuckets();


    window.RISE_SKIN
      .applyPrimaryConcernScores(
        scores,
        profile.concerns
      );

    window.RISE_SKIN
      .applySkinBehaviorScores(
        scores,
        profile
      );

    window.RISE_SKIN
      .applyAcneScores(
        scores,
        profile
      );

    window.RISE_SKIN
      .applyPigmentScores(
        scores,
        profile
      );

    window.RISE_SKIN
      .applyRednessScores(
        scores,
        profile
      );

    window.RISE_SKIN
      .applyAgingScores(
        scores,
        profile
      );

    window.RISE_SKIN
      .applyEyeScores(
        scores,
        profile
      );

    window.RISE_SKIN
      .applySunScores(
        scores,
        profile
      );


    return {
      profile,
      scores
    };
  };


// ============================================================
// HARD PRODUCT GATES
// ============================================================

window.RISE_SKIN.evaluateProductGate =
  function (product, profile) {

    const gates =
      product.gates || {};

    const reasons = [];


    // --------------------------------------------------------
    // PREGNANCY
    // --------------------------------------------------------

    if (
      profile.pregnant &&
      gates.pregnancy === "block"
    ) {

      reasons.push(
        "Blocked for pregnancy / trying to conceive"
      );
    }


    // --------------------------------------------------------
    // PRESCRIPTION RETINOID
    // --------------------------------------------------------

    if (
      profile.prescriptionRetinoid &&
      gates.prescriptionRetinoid === "block"
    ) {

      reasons.push(
        "Blocked because client reports prescription retinoid / acne medication"
      );
    }


    // --------------------------------------------------------
    // IMPAIRED BARRIER
    // --------------------------------------------------------

    if (
      profile.barrierStatus === "impaired" &&
      gates.barrierImpaired === "defer"
    ) {

      reasons.push(
        "Deferred because barrier is currently impaired"
      );
    }


    // --------------------------------------------------------
    // SEVERE SENSITIVITY
    // --------------------------------------------------------

    if (
      profile.sensitivity === "very_sensitive" &&
      gates.severeSensitivity === "defer"
    ) {

      reasons.push(
        "Deferred because client reports very sensitive skin"
      );
    }


   return {
  blocked:
    reasons.length > 0,

  deprioritize: false,

  reasons
   };
};
  


// ============================================================
// PRODUCT CONCERN SCORE
// ============================================================

window.RISE_SKIN.getProductConcernScore =
  function (product, clientScores) {

    const productConcerns =
      product.concerns || {};

    let total = 0;
    const reasons = [];


    const keys = [
      "acne",
      "pores",
      "texture",
      "pigment",
      "redness",
      "fineLines",
      "laxity",
      "dullness",
      "dehydration",
      "congestion",
      "underEye"
    ];


    keys.forEach(
      (key) => {

        const productWeight =
          Number(
            productConcerns[key] || 0
          );

        const clientWeight =
          Number(
            clientScores[key] || 0
          );


        if (
          productWeight > 0 &&
          clientWeight > 0
        ) {

          const contribution =
            productWeight *
            clientWeight *
            window.RISE_SKIN
              .engineConfig
              .concernWeight;

          total += contribution;


          reasons.push({
            type: "concern",
            key,
            productWeight,
            clientWeight,
            contribution
          });
        }
      }
    );


    return {
      total,
      reasons
    };
  };


// ============================================================
// PRODUCT FIT SCORE
// ============================================================

window.RISE_SKIN.getProductFitScore =
  function (product, profile) {

    const fit =
      product.fit || {};

    let total = 0;
    const reasons = [];


    // --------------------------------------------------------
    // SKIN TYPE
    // --------------------------------------------------------

    const behaviorToFit = {
      dry: "dry",
      balanced: "balanced",
      combination: "combination",
      oily: "oily"
    };


    const fitKey =
      behaviorToFit[
        profile.skinBehavior
      ];


    if (
      fitKey &&
      fit[fitKey] !== undefined
    ) {

      const contribution =
        Number(fit[fitKey]) *
        window.RISE_SKIN
          .engineConfig
          .fitWeight;

      total += contribution;

      reasons.push({
        type: "skinFit",
        key: fitKey,
        contribution
      });
    }


    // --------------------------------------------------------
    // SENSITIVITY / RESILIENCE
    // --------------------------------------------------------

    if (
      profile.sensitivity ===
        "resilient" &&
      fit.resilient !== undefined
    ) {

      const contribution =
        Number(fit.resilient);

      total += contribution;

      reasons.push({
        type: "fit",
        key: "resilient",
        contribution
      });
    }


    if (
      (
        profile.sensitivity ===
          "sensitive" ||
        profile.sensitivity ===
          "very_sensitive"
      ) &&
      fit.sensitive !== undefined
    ) {

      const contribution =
        Number(fit.sensitive);

      total += contribution;

      reasons.push({
        type: "fit",
        key: "sensitive",
        contribution
      });
    }


    // --------------------------------------------------------
    // ACNE-PRONE
    // --------------------------------------------------------

    if (
      profile.acneSeverity &&
      ![
        "rare",
        null
      ].includes(
        profile.acneSeverity
      ) &&
      fit.acneProne !== undefined
    ) {

      const contribution =
        Number(fit.acneProne);

      total += contribution;

      reasons.push({
        type: "fit",
        key: "acneProne",
        contribution
      });
    }


    return {
      total,
      reasons
    };
  };


// ============================================================
// SPECIAL PRODUCT MODIFIERS
// ============================================================

window.RISE_SKIN.getProductModifiers =
  function (
    product,
    profile,
    clientScores
  ) {

    let total = 0;
    const reasons = [];


    // --------------------------------------------------------
    // SPF
    // --------------------------------------------------------

    if (
      product.routineSlot === "spf"
    ) {

      total += 20;

      reasons.push({
        type: "modifier",
        key: "mandatory_spf",
        contribution: 20
      });


      if (
        clientScores.uvExposure >= 4
      ) {

        total += 5;

        reasons.push({
          type: "modifier",
          key: "high_uv",
          contribution: 5
        });
      }


      const tags =
        product.reasonTags || [];

      profile.spfPreference
        .forEach(
          (preference) => {

            const preferenceTagMap = {
              tinted: "TINTED",
              sheer: "SHEER",
              glowy: "GLOW",
              matte: "MATTE",
              reapplication:
                "REAPPLICATION"
            };

            const tag =
              preferenceTagMap[
                preference
              ];

            if (
              tag &&
              tags.includes(tag)
            ) {

              total += 10;

              reasons.push({
                type: "modifier",
                key:
                  `spf_preference_${preference}`,
                contribution: 10
              });
            }
          }
        );
    }


   

    // --------------------------------------------------------
    // UNDER-EYE PRODUCTS
    // --------------------------------------------------------

    if (
      product.routineSlot === "eye" &&
      clientScores.underEye > 0
    ) {

      total += 8;

      reasons.push({
        type: "modifier",
        key:
          "under_eye_selected",
        contribution: 8
      });
    }


    return {
      total,
      reasons
    };
  };


// ============================================================
// SCORE ONE PRODUCT
// ============================================================

window.RISE_SKIN.scoreProduct =
  function (
    product,
    profile,
    clientScores
  ) {

    const gate =
      window.RISE_SKIN
        .evaluateProductGate(
          product,
          profile
        );


    if (gate.blocked) {

      return {
        product,
        score:
          window.RISE_SKIN
            .engineConfig
            .blockedScore,

        eligible: false,

        gate,

        concernScore: 0,
        fitScore: 0,
        modifierScore: 0,

        reasons: []
      };
    }


    const concern =
      window.RISE_SKIN
        .getProductConcernScore(
          product,
          clientScores
        );


    const fit =
      window.RISE_SKIN
        .getProductFitScore(
          product,
          profile
        );


    const modifiers =
      window.RISE_SKIN
        .getProductModifiers(
          product,
          profile,
          clientScores
        );


    let score =
      concern.total +
      fit.total +
      modifiers.total;


    if (gate.deprioritize) {

      score -= 10;
    }


    return {
      product,
      score,

      eligible:
        score >=
        window.RISE_SKIN
          .engineConfig
          .minimumProductScore,

      gate,

      concernScore:
        concern.total,

      fitScore:
        fit.total,

      modifierScore:
        modifiers.total,

      reasons: [
        ...concern.reasons,
        ...fit.reasons,
        ...modifiers.reasons
      ]
    };
  };


// ============================================================
// SCORE ALL PRODUCTS
// ============================================================

window.RISE_SKIN.rankProducts =
  function (profileInput) {

    const engineProfile =
      window.RISE_SKIN
        .buildClientScores(
          profileInput
        );


    const products =
      window.RISE_SKIN_PRODUCTS || [];


    const ranked =
      products
        .map(
          (product) =>
            window.RISE_SKIN
              .scoreProduct(
                product,
                engineProfile.profile,
                engineProfile.scores
              )
        )
        .sort(
          (a, b) =>
            b.score - a.score
        );


    return {
      profile:
        engineProfile.profile,

      scores:
        engineProfile.scores,

      ranked,

      eligible:
        ranked.filter(
          (item) =>
            item.eligible
        ),

      blocked:
        ranked.filter(
          (item) =>
            !item.eligible &&
            item.gate &&
            item.gate.blocked
        )
    };
  };


// ============================================================
// DEBUG TOP PRODUCTS
// ============================================================

window.RISE_SKIN.debugRankProducts =
  function (
    profileInput,
    limit = 15
  ) {

    const result =
      window.RISE_SKIN
        .rankProducts(
          profileInput
        );


    const table =
      result.eligible
        .slice(0, limit)
        .map(
          (item) => ({
            product:
              item.product.name,

            brand:
              item.product.brand,

            slot:
              item.product
                .routineSlot,

            score:
              Math.round(
                item.score * 10
              ) / 10,

            concern:
              Math.round(
                item.concernScore *
                10
              ) / 10,

            fit:
              Math.round(
                item.fitScore * 10
              ) / 10,

            modifiers:
              Math.round(
                item.modifierScore *
                10
              ) / 10
          })
        );


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN ENGINE — PRODUCT RANKING"
      );

      console.log(
        "Normalized profile:",
        result.profile
      );

      console.log(
        "Client scores:",
        result.scores
      );

      console.table(table);

      console.log(
        "Blocked products:",
        result.blocked.map(
          (item) => ({
            product:
              item.product.name,

            reasons:
              item.gate.reasons
          })
        )
      );

      console.groupEnd();
    }


    return result;
  };


// ============================================================
// ENGINE VALIDATION
// ============================================================

window.RISE_SKIN.validateEngine =
  function () {

    const required = [
      "rankProducts",
      "scoreProduct",
      "buildClientScores"
    ];


    const missing =
      required.filter(
        (name) =>
          typeof window.RISE_SKIN[
            name
          ] !== "function"
      );


    const report = {
      valid:
        missing.length === 0,

      missingFunctions:
        missing
    };


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN CONCIERGE — ENGINE VALIDATION"
      );


      if (report.valid) {

        console.log(
          "Recommendation engine foundation loaded."
        );

      } else {

        console.error(
          "Missing engine functions:",
          missing
        );
      }


      console.groupEnd();
    }


    return report;
  };


// ============================================================
// INITIAL DEBUG CHECK
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Recommendation Engine Part 1 loaded"
  );

  window.RISE_SKIN
    .validateEngine();
}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Recommendation Engine — Part 2
 * Routine Assembly
 * ============================================================
 *
 * This layer:
 * - Converts ranked candidates into coherent routines
 * - Builds Essential / Complete / Elevated
 * - Selects one winner per functional role
 * - Prevents duplicate corrective categories
 * - Controls retinoid / exfoliant stacking
 * - Separates provider-guided products
 * - Adds products only when clinically / cosmetically justified
 * ============================================================
 */


// ============================================================
// ROUTINE CONFIG
// ============================================================

window.RISE_SKIN.routineConfig = {

  levels: {
    simple: {
      label: "Essential",
      maxAM: 4,
      maxPM: 4
    },

    complete: {
      label: "Complete",
      maxAM: 6,
      maxPM: 6
    },

    elevated: {
      label: "Elevated",
      maxAM: 8,
      maxPM: 8
    }
  },

  // Only one winner from each competing family.
  exclusiveFamilies: [
    "cleanser",
    "antioxidant",
    "retinoid",
    "exfoliant",
    "acne_corrective",
    "pigment_corrective",
    "moisturizer",
    "eye",
    "spf"
  ]
};


// ============================================================
// PRODUCT HELPERS
// ============================================================

window.RISE_SKIN.productArray =
  function () {

    return window.RISE_SKIN_PRODUCTS || [];
  };


window.RISE_SKIN.getProductId =
  function (product) {

    return (
      product.id ||
      product.handle ||
      product.name
    );
  };


window.RISE_SKIN.getActiveClasses =
  function (product) {

    return window.RISE_SKIN.asArray(
      product.activeClasses
    );
  };


window.RISE_SKIN.getReasonTags =
  function (product) {

    return window.RISE_SKIN.asArray(
      product.reasonTags
    );
  };


// ============================================================
// PROVIDER REVIEW
// ============================================================

window.RISE_SKIN.requiresProviderReview =
  function (product) {

    return (
      product.providerReview === true ||
      product.requiresProviderReview === true ||
      product.gates?.providerReview === true
    );
  };


// ============================================================
// DETERMINE FUNCTIONAL FAMILY
// ============================================================

window.RISE_SKIN.getProductFamily =
  function (product) {

    const slot =
      product.routineSlot || "";

    const actives =
      window.RISE_SKIN
        .getActiveClasses(product);

    const tags =
      window.RISE_SKIN
        .getReasonTags(product);


    // Explicit family always wins.
    if (product.family) {
      return product.family;
    }


    // Core slots.
    if (slot === "cleanser") {
      return "cleanser";
    }

    if (slot === "spf") {
      return "spf";
    }

    if (slot === "eye") {
      return "eye";
    }

    if (
      slot === "moisturizer" ||
      slot === "hydrate"
    ) {
      return "moisturizer";
    }

    if (slot === "antioxidant") {
      return "antioxidant";
    }


    // Active classes.
    if (
      actives.includes("retinoid")
    ) {
      return "retinoid";
    }

    if (
      actives.includes("aha") ||
      actives.includes("bha") ||
      actives.includes("exfoliant") ||
      tags.includes("EXFOLIATION")
    ) {
      return "exfoliant";
    }

    if (
      actives.includes("benzoyl_peroxide") ||
      tags.includes("ACNE")
    ) {
      return "acne_corrective";
    }


    // Product concern metadata.
    if (
      Number(
        product.concerns?.pigment || 0
      ) >= 4
    ) {
      return "pigment_corrective";
    }


    // Regenerative / growth-factor / peptide type products.
    if (
      slot === "regenerative" ||
      tags.includes("REGENERATIVE") ||
      tags.includes("GROWTH_FACTOR")
    ) {
      return "regenerative";
    }


    // General corrective.
    if (slot === "corrective") {
      return "corrective";
    }


    if (slot === "support") {
      return "support";
    }


    return slot || "other";
  };


// ============================================================
// WHEN SHOULD A SLOT EXIST?
// ============================================================

window.RISE_SKIN.getRoutineNeeds =
  function (
    profile,
    scores
  ) {

    const needs = {

      cleanser: true,
      spf: true,

      antioxidant: false,
      corrective: false,
      retinoid: false,
      exfoliant: false,
      acne_corrective: false,
      pigment_corrective: false,

      moisturizer: false,
      eye: false,
      regenerative: false,
      support: false
    };


    // Antioxidant is broadly useful when correction,
    // prevention, pigment, redness, or UV exposure matters.
    if (
      scores.pigment > 0 ||
      scores.redness > 0 ||
      scores.fineLines > 0 ||
      scores.dullness > 0 ||
      scores.uvExposure > 1
    ) {
      needs.antioxidant = true;
    }


    // General corrective need.
    if (
      scores.acne > 0 ||
      scores.pores > 0 ||
      scores.texture > 0 ||
      scores.pigment > 0 ||
      scores.redness > 0 ||
      scores.fineLines > 0 ||
      scores.laxity > 0 ||
      scores.dullness > 0
    ) {
      needs.corrective = true;
    }


    // Acne.
    if (
      scores.acne >= 4 ||
      scores.congestion >= 4
    ) {
      needs.acne_corrective = true;
    }


    // Pigment.
    if (scores.pigment >= 4) {
      needs.pigment_corrective = true;
    }


    // Eye.
    if (scores.underEye >= 3) {
      needs.eye = true;
    }


    // Moisturizer.
    if (
      scores.dryness >= 3 ||
      scores.dehydration >= 3 ||
      scores.sensitivity >= 3 ||
      profile.skinBehavior === "dry"
    ) {
      needs.moisturizer = true;
    }


    // Retinoid.
    if (
      (
        scores.fineLines >= 3 ||
        scores.texture >= 4 ||
        scores.pores >= 4 ||
        scores.acne >= 4
      ) &&
      !profile.pregnant &&
      !profile.prescriptionRetinoid &&
      profile.barrierStatus !== "impaired"
    ) {
      needs.retinoid = true;
    }


    // Exfoliant.
    if (
      (
        scores.congestion >= 4 ||
        scores.texture >= 4 ||
        scores.pigment >= 5
      ) &&
      scores.sensitivity < 4 &&
      profile.barrierStatus !== "impaired"
    ) {
      needs.exfoliant = true;
    }


    // Regenerative becomes an elevated opportunity.
    if (
      scores.fineLines >= 3 ||
      scores.laxity >= 3 ||
      scores.redness >= 4 ||
      scores.dehydration >= 3
    ) {
      needs.regenerative = true;
    }


    if (
      scores.sensitivity >= 3 ||
      scores.redness >= 4 ||
      scores.dehydration >= 3
    ) {
      needs.support = true;
    }


    return needs;
  };


// ============================================================
// GET CANDIDATES BY FAMILY
// ============================================================

window.RISE_SKIN.getCandidatesByFamily =
  function (
    ranking,
    family,
    options = {}
  ) {

    const {
      allowProviderReview = false
    } = options;


    return ranking.eligible.filter(
      (item) => {

        const product =
          item.product;

        if (
          window.RISE_SKIN
            .getProductFamily(product)
          !== family
        ) {
          return false;
        }


        if (
          !allowProviderReview &&
          window.RISE_SKIN
            .requiresProviderReview(
              product
            )
        ) {
          return false;
        }


        return true;
      }
    );
  };


// ============================================================
// PICK BEST PRODUCT IN FAMILY
// ============================================================

window.RISE_SKIN.pickBestProduct =
  function (
    ranking,
    family,
    options = {}
  ) {

    const candidates =
      window.RISE_SKIN
        .getCandidatesByFamily(
          ranking,
          family,
          options
        );


    return candidates.length
      ? candidates[0]
      : null;
  };


// ============================================================
// PROVIDER-GUIDED RECOMMENDATIONS
// ============================================================

window.RISE_SKIN.getProviderGuidedMatches =
  function (ranking) {

    return ranking.eligible
      .filter(
        (item) =>
          window.RISE_SKIN
            .requiresProviderReview(
              item.product
            )
      )
      .slice(0, 3);
  };


// ============================================================
// DUPLICATE CHECK
// ============================================================

window.RISE_SKIN.routineHasProduct =
  function (
    routine,
    product
  ) {

    const id =
      window.RISE_SKIN
        .getProductId(product);

    return [
      ...routine.am,
      ...routine.pm
    ].some(
      (item) =>
        window.RISE_SKIN
          .getProductId(
            item.product
          ) === id
    );
  };


// ============================================================
// FAMILY CHECK
// ============================================================

window.RISE_SKIN.routineHasFamily =
  function (
    routine,
    family
  ) {

    return [
      ...routine.am,
      ...routine.pm
    ].some(
      (item) =>
        item.family === family
    );
  };


// ============================================================
// ADD PRODUCT TO ROUTINE
// ============================================================

window.RISE_SKIN.addRoutineProduct =
  function (
    routine,
    scoredItem,
    timing,
    family,
    reason
  ) {

    if (!scoredItem) {
      return false;
    }


    if (
      window.RISE_SKIN
        .routineHasProduct(
          routine,
          scoredItem.product
        )
    ) {
      return false;
    }


    const entry = {
      product:
        scoredItem.product,

      score:
        scoredItem.score,

      family,

      timing,

      reason:
        reason || null,

      providerReview:
        window.RISE_SKIN
          .requiresProviderReview(
            scoredItem.product
          )
    };


    routine[timing].push(entry);

    return true;
  };


// ============================================================
// DETERMINE PRODUCT TIMING
// ============================================================

window.RISE_SKIN.getPreferredTiming =
  function (
    product,
    family
  ) {

    const usage =
      window.RISE_SKIN.asArray(
        product?.amPm
      )
      .map(
        value =>
          String(value).toUpperCase()
      );


    const hasAM =
      usage.includes("AM");

    const hasPM =
      usage.includes("PM");


    /* --------------------------------------------------------
       PRODUCT-SPECIFIC USAGE IS THE SOURCE OF TRUTH
       -------------------------------------------------------- */

    if (hasAM && hasPM) {
      return "both";
    }

    if (hasAM) {
      return "am";
    }

    if (hasPM) {
      return "pm";
    }


    /* --------------------------------------------------------
       NON-DAILY / SPECIALTY PRODUCTS
       Do not force them into a daily routine.
       -------------------------------------------------------- */

    if (
      usage.includes("INTERMITTENT") ||
      usage.includes("AS_NEEDED") ||
      usage.includes("AS_DIRECTED") ||
      usage.includes("REAPPLY")
    ) {
      return "specialty";
    }


    /* --------------------------------------------------------
       FALLBACK ONLY IF PRODUCT DATA HAS NO USAGE TIMING
       -------------------------------------------------------- */

    switch (family) {

      case "cleanser":
        return "both";

      case "spf":
        return "am";

      case "antioxidant":
        return "am";

      case "retinoid":
        return "pm";

      case "exfoliant":
        return "pm";

      case "acne_corrective":
        return "pm";

      case "pigment_corrective":
        return "pm";

      case "corrective":
        return "pm";

      case "eye":
        return "both";

      case "moisturizer":
        return "both";

      case "regenerative":
        return "pm";

      case "support":
        return "both";

      default:
        return "pm";
    }
  };

// ============================================================
// ADD WITH TIMING
// ============================================================

window.RISE_SKIN.addWithPreferredTiming =
  function (
    routine,
    item,
    family,
    reason
  ) {

    if (!item) {
      return;
    }


    const timing =
      window.RISE_SKIN
        .getPreferredTiming(
          item.product,
          family
        );

        if (timing === "specialty") {
  return;
}


    if (timing === "both") {

      window.RISE_SKIN
        .addRoutineProduct(
          routine,
          item,
          "am",
          family,
          reason
        );


      // Same product can intentionally appear
      // in both AM and PM.
      routine.pm.push({
        product: item.product,
        score: item.score,
        family,
        timing: "pm",
        reason,
        providerReview:
          window.RISE_SKIN
            .requiresProviderReview(
              item.product
            )
      });

      return;
    }


    window.RISE_SKIN
      .addRoutineProduct(
        routine,
        item,
        timing,
        family,
        reason
      );
  };


// ============================================================
// ACTIVE CONFLICT CHECK
// ============================================================

window.RISE_SKIN.hasStrongPMActive =
  function (routine) {

    return routine.pm.some(
      (item) =>
        [
          "retinoid",
          "exfoliant",
          "acne_corrective"
        ].includes(
          item.family
        )
    );
  };


// ============================================================
// CREATE EMPTY ROUTINE
// ============================================================

window.RISE_SKIN.createEmptyRoutine =
  function (level) {

    return {
      level,

      label:
        window.RISE_SKIN
          .routineConfig
          .levels[level]
          .label,

      am: [],
      pm: [],

      providerGuided: [],

      notes: []
    };
  };


// ============================================================
// ESSENTIAL ROUTINE
// ============================================================

window.RISE_SKIN.buildEssentialRoutine =
  function (
    ranking,
    needs
  ) {

    const routine =
      window.RISE_SKIN
        .createEmptyRoutine(
          "simple"
        );


    // Cleanser.
    const cleanser =
      window.RISE_SKIN
        .pickBestProduct(
          ranking,
          "cleanser"
        );

    window.RISE_SKIN
      .addWithPreferredTiming(
        routine,
        cleanser,
        "cleanser",
        "Foundation cleanser"
      );


    // Highest-impact treatment step.
    const correctiveFamilies = [];

    if (needs.acne_corrective) {
      correctiveFamilies.push(
        "acne_corrective"
      );
    }

    if (needs.pigment_corrective) {
      correctiveFamilies.push(
        "pigment_corrective"
      );
    }

    if (needs.retinoid) {
      correctiveFamilies.push(
        "retinoid"
      );
    }

    if (needs.antioxidant) {
      correctiveFamilies.push(
        "antioxidant"
      );
    }

    correctiveFamilies.push(
      "corrective"
    );


    const correctiveCandidates =
      correctiveFamilies
        .map(
          (family) => ({
            family,
            item:
              window.RISE_SKIN
                .pickBestProduct(
                  ranking,
                  family
                )
          })
        )
        .filter(
          (entry) =>
            entry.item
        )
        .sort(
          (a, b) =>
            b.item.score -
            a.item.score
        );


    if (
      correctiveCandidates.length
    ) {

      const winner =
        correctiveCandidates[0];

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          winner.item,
          winner.family,
          "Highest-impact corrective"
        );
    }


    // Moisturizer only when justified.
    if (needs.moisturizer) {

      const moisturizer =
        window.RISE_SKIN
          .pickBestProduct(
            ranking,
            "moisturizer"
          );

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          moisturizer,
          "moisturizer",
          "Barrier and hydration support"
        );
    }


    // SPF always.
    const spf =
      window.RISE_SKIN
        .pickBestProduct(
          ranking,
          "spf"
        );

    window.RISE_SKIN
      .addWithPreferredTiming(
        routine,
        spf,
        "spf",
        "Daily UV protection"
      );


    return routine;
  };


// ============================================================
// COMPLETE ROUTINE
// ============================================================

window.RISE_SKIN.buildCompleteRoutine =
  function (
    ranking,
    needs
  ) {

    const routine =
      window.RISE_SKIN
        .createEmptyRoutine(
          "complete"
        );


    // Cleanser.
    window.RISE_SKIN
      .addWithPreferredTiming(
        routine,
        window.RISE_SKIN
          .pickBestProduct(
            ranking,
            "cleanser"
          ),
        "cleanser",
        "Foundation cleanser"
      );


    // Antioxidant.
    if (needs.antioxidant) {

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          window.RISE_SKIN
            .pickBestProduct(
              ranking,
              "antioxidant"
            ),
          "antioxidant",
          "Daily antioxidant protection"
        );
    }


    // Choose ONE primary PM corrective family.
    const pmFamilies = [];

    if (needs.acne_corrective) {
      pmFamilies.push(
        "acne_corrective"
      );
    }

    if (needs.pigment_corrective) {
      pmFamilies.push(
        "pigment_corrective"
      );
    }

    if (needs.retinoid) {
      pmFamilies.push(
        "retinoid"
      );
    }

    if (needs.exfoliant) {
      pmFamilies.push(
        "exfoliant"
      );
    }

    pmFamilies.push(
      "corrective"
    );


    const pmCandidates =
      pmFamilies
        .map(
          (family) => ({
            family,
            item:
              window.RISE_SKIN
                .pickBestProduct(
                  ranking,
                  family
                )
          })
        )
        .filter(
          (entry) =>
            entry.item
        )
        .sort(
          (a, b) =>
            b.item.score -
            a.item.score
        );


    if (pmCandidates.length) {

      const winner =
        pmCandidates[0];

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          winner.item,
          winner.family,
          "Primary corrective"
        );
    }


    // Eye.
    if (needs.eye) {

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          window.RISE_SKIN
            .pickBestProduct(
              ranking,
              "eye"
            ),
          "eye",
          "Targeted eye support"
        );
    }


    // Moisturizer.
    if (needs.moisturizer) {

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          window.RISE_SKIN
            .pickBestProduct(
              ranking,
              "moisturizer"
            ),
          "moisturizer",
          "Hydration and barrier support"
        );
    }


    // SPF.
    window.RISE_SKIN
      .addWithPreferredTiming(
        routine,
        window.RISE_SKIN
          .pickBestProduct(
            ranking,
            "spf"
          ),
        "spf",
        "Daily UV protection"
      );


    return routine;
  };


// ============================================================
// ELEVATED ROUTINE
// ============================================================

window.RISE_SKIN.buildElevatedRoutine =
  function (
    ranking,
    needs
  ) {

    // Start with the complete logic.
    const routine =
      window.RISE_SKIN
        .buildCompleteRoutine(
          ranking,
          needs
        );


    routine.level =
      "elevated";

    routine.label =
      "Elevated";


    // Add regenerative layer if useful.
    if (
      needs.regenerative &&
      !window.RISE_SKIN
        .routineHasFamily(
          routine,
          "regenerative"
        )
    ) {

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          window.RISE_SKIN
            .pickBestProduct(
              ranking,
              "regenerative"
            ),
          "regenerative",
          "Advanced regenerative support"
        );
    }


    // Add support layer if useful.
    if (
      needs.support &&
      !window.RISE_SKIN
        .routineHasFamily(
          routine,
          "support"
        )
    ) {

      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          window.RISE_SKIN
            .pickBestProduct(
              ranking,
              "support"
            ),
          "support",
          "Additional barrier and recovery support"
        );
    }


    // We may add a second corrective only if
    // it doesn't create an aggressive PM stack.
    if (
      !window.RISE_SKIN
        .hasStrongPMActive(
          routine
        )
    ) {

      if (
        needs.retinoid &&
        !window.RISE_SKIN
          .routineHasFamily(
            routine,
            "retinoid"
          )
      ) {

        window.RISE_SKIN
          .addWithPreferredTiming(
            routine,
            window.RISE_SKIN
              .pickBestProduct(
                ranking,
                "retinoid"
              ),
            "retinoid",
            "Advanced evening correction"
          );
      }
    }


    return routine;
  };


// ============================================================
// SORT ROUTINE INTO APPLICATION ORDER
// ============================================================

window.RISE_SKIN.sortRoutine =
  function (routine) {

    const order = {
      cleanser: 10,
      support: 20,
      antioxidant: 30,
      regenerative: 35,
      eye: 40,
      corrective: 50,
      pigment_corrective: 50,
      acne_corrective: 50,
      exfoliant: 50,
      retinoid: 50,
      moisturizer: 70,
      spf: 100
    };


    routine.am.sort(
      (a, b) =>
        (order[a.family] || 60) -
        (order[b.family] || 60)
    );


    routine.pm.sort(
      (a, b) =>
        (order[a.family] || 60) -
        (order[b.family] || 60)
    );


    return routine;
  };


// ============================================================
// BUILD ALL THREE ROUTINES
// ============================================================

window.RISE_SKIN.buildRoutines =
  function (profileInput) {

    const ranking =
      window.RISE_SKIN
        .rankProducts(
          profileInput
        );


    const needs =
      window.RISE_SKIN
        .getRoutineNeeds(
          ranking.profile,
          ranking.scores
        );


    const simple =
      window.RISE_SKIN
        .sortRoutine(
          window.RISE_SKIN
            .buildEssentialRoutine(
              ranking,
              needs
            )
        );


    const complete =
      window.RISE_SKIN
        .sortRoutine(
          window.RISE_SKIN
            .buildCompleteRoutine(
              ranking,
              needs
            )
        );


    const elevated =
      window.RISE_SKIN
        .sortRoutine(
          window.RISE_SKIN
            .buildElevatedRoutine(
              ranking,
              needs
            )
        );


    const providerGuided =
      window.RISE_SKIN
        .getProviderGuidedMatches(
          ranking
        );


    simple.providerGuided =
      providerGuided;

    complete.providerGuided =
      providerGuided;

    elevated.providerGuided =
      providerGuided;


    return {
      recommendedLevel:
        ranking.profile
          .routineCommitment ||
        "complete",

      profile:
        ranking.profile,

      scores:
        ranking.scores,

      needs,

      ranking,

      routines: {
        simple,
        complete,
        elevated
      },

      providerGuided
    };
  };


// ============================================================
// DEBUG ROUTINE TABLE
// ============================================================

window.RISE_SKIN.debugRoutine =
  function (
    routine
  ) {

    const rows = [];


    routine.am.forEach(
      (item, index) => {

        rows.push({
          time: "AM",
          step: index + 1,
          product:
            item.product.name,
          brand:
            item.product.brand,
          family:
            item.family,
          score:
            Math.round(
              item.score * 10
            ) / 10,
          reason:
            item.reason
        });
      }
    );


    routine.pm.forEach(
      (item, index) => {

        rows.push({
          time: "PM",
          step: index + 1,
          product:
            item.product.name,
          brand:
            item.product.brand,
          family:
            item.family,
          score:
            Math.round(
              item.score * 10
            ) / 10,
          reason:
            item.reason
        });
      }
    );


    console.table(rows);

    return rows;
  };


// ============================================================
// DEBUG ALL ROUTINES
// ============================================================

window.RISE_SKIN.debugBuildRoutines =
  function (profileInput) {

    const result =
      window.RISE_SKIN
        .buildRoutines(
          profileInput
        );


    console.group(
      "RISE SKIN ENGINE — ROUTINE ASSEMBLY"
    );


    console.log(
      "Recommended level:",
      result.recommendedLevel
    );


    console.log(
      "Routine needs:",
      result.needs
    );


    console.group(
      "ESSENTIAL"
    );

    window.RISE_SKIN
      .debugRoutine(
        result.routines.simple
      );

    console.groupEnd();


    console.group(
      "COMPLETE"
    );

    window.RISE_SKIN
      .debugRoutine(
        result.routines.complete
      );

    console.groupEnd();


    console.group(
      "ELEVATED"
    );

    window.RISE_SKIN
      .debugRoutine(
        result.routines.elevated
      );

    console.groupEnd();


    console.log(
      "Provider-guided matches:",
      result.providerGuided.map(
        (item) => ({
          product:
            item.product.name,
          brand:
            item.product.brand,
          score:
            item.score
        })
      )
    );


    console.groupEnd();


    return result;
  };


// ============================================================
// PART 2 VALIDATION
// ============================================================

window.RISE_SKIN.validateRoutineEngine =
  function () {

    const required = [
      "buildRoutines",
      "buildEssentialRoutine",
      "buildCompleteRoutine",
      "buildElevatedRoutine",
      "getProductFamily"
    ];


    const missing =
      required.filter(
        (name) =>
          typeof window.RISE_SKIN[
            name
          ] !== "function"
      );


    const report = {
      valid:
        missing.length === 0,

      missingFunctions:
        missing
    };


    if (window.RISE_DEBUG) {

      console.group(
        "RISE SKIN CONCIERGE — ROUTINE ENGINE VALIDATION"
      );


      if (report.valid) {

        console.log(
          "Routine assembly engine loaded."
        );

      } else {

        console.error(
          "Missing routine functions:",
          missing
        );
      }


      console.groupEnd();
    }


    return report;
  };


// ============================================================
// INITIAL PART 2 CHECK
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Recommendation Engine Part 2 loaded"
  );

  window.RISE_SKIN
    .validateRoutineEngine();
}
/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Recommendation Engine — Part 2B
 * Routine Architecture Refinements
 * ============================================================
 *
 * STRUCTURAL RULES:
 *
 * ALL LEVELS
 * - Cleanser every AM
 * - Cleanser every PM
 * - SPF every AM
 *
 * COMPLETE
 * - Cleanser AM + PM
 * - Antioxidant AM
 * - SPF AM
 * - Retinoid PM when eligible
 * - Safety-approved corrective substitute when retinoid
 *   is contraindicated / deferred
 *
 * ELEVATED
 * - Inherits Complete foundation
 * - Adds only distinct, justified optimization
 * - Avoids unnecessary AM/PM duplication
 * ============================================================
 */


// ============================================================
// CORE ROUTINE REQUIREMENTS
// ============================================================

window.RISE_SKIN.coreRoutineRules = {

  simple: {
    cleanserAM: true,
    cleanserPM: true,
    antioxidantAM: true,
    correctivePM: true,
    hydratorWithRetinoidPM: true,
    spfAM: true
  },

  complete: {
    cleanserAM: true,
    cleanserPM: true,
    antioxidantAM: true,
    targetedSerumAM: true,
    correctivePM: true,
    retinoidPM: true,
    hydratorPM: true,
    spfAM: true
  },

  elevated: {
    cleanserAM: true,
    cleanserPM: true,
    antioxidantAM: true,
    targetedSerumAM: true,
    eyeIfIndicated: true,
    lipRequired: true,
    neckRequired: true,
    regenerativeIfIndicated: true,
    correctivePM: true,
    retinoidPM: true,
    hydratorPM: true,
    spfAM: true
  }

};

// ============================================================
// REMOVE FAMILY FROM A SPECIFIC TIME
// ============================================================

window.RISE_SKIN.removeFamilyFromTiming =
  function (routine, timing, family) {

    routine[timing] =
      routine[timing].filter(
        (item) =>
          item.family !== family
      );
  };


// ============================================================
// FIND EXISTING FAMILY
// ============================================================

window.RISE_SKIN.findRoutineFamily =
  function (routine, timing, family) {

    return routine[timing].find(
      (item) =>
        item.family === family
    ) || null;
  };


// ============================================================
// FORCE CORE PRODUCT INTO SPECIFIC TIME
// ============================================================

window.RISE_SKIN.ensureRoutineProduct =
  function (
    routine,
    ranking,
    family,
    timing,
    reason
  ) {

    const existing =
      window.RISE_SKIN
        .findRoutineFamily(
          routine,
          timing,
          family
        );


    if (existing) {
      return existing;
    }


    const item =
      window.RISE_SKIN
        .pickBestProduct(
          ranking,
          family
        );


    if (!item) {
      return null;
    }


    routine[timing].push({
      product: item.product,
      score: item.score,
      family,
      timing,
      reason,
      providerReview:
        window.RISE_SKIN
          .requiresProviderReview(
            item.product
          )
    });


    return item;
  };


// ============================================================
// ENSURE CLEANSER AM + PM
// ============================================================

window.RISE_SKIN.ensureDailyCleanser =
  function (
    routine,
    ranking
  ) {

    window.RISE_SKIN
      .ensureRoutineProduct(
        routine,
        ranking,
        "cleanser",
        "am",
        "Daily morning cleanse"
      );


    window.RISE_SKIN
      .ensureRoutineProduct(
        routine,
        ranking,
        "cleanser",
        "pm",
        "Daily evening cleanse"
      );
  };


// ============================================================
// ENSURE SPF EVERY MORNING
// ============================================================

window.RISE_SKIN.ensureDailySPF =
  function (
    routine,
    ranking
  ) {

    window.RISE_SKIN
      .ensureRoutineProduct(
        routine,
        ranking,
        "spf",
        "am",
        "Daily UV protection"
      );


    // SPF should never appear in PM.
    window.RISE_SKIN
      .removeFamilyFromTiming(
        routine,
        "pm",
        "spf"
      );
  };


// ============================================================
// ENSURE ANTIOXIDANT EVERY MORNING
// COMPLETE + ELEVATED
// ============================================================

window.RISE_SKIN.ensureMorningAntioxidant =
  function (
    routine,
    ranking
  ) {

    window.RISE_SKIN
      .ensureRoutineProduct(
        routine,
        ranking,
        "antioxidant",
        "am",
        "Daily antioxidant protection"
      );
  };

// ============================================================
// ENSURE PM MOISTURIZER / BARRIER SUPPORT
// ============================================================

window.RISE_SKIN.ensureEveningMoisturizer =
  function (
    routine,
    ranking
  ) {

    window.RISE_SKIN
      .ensureRoutineProduct(
        routine,
        ranking,
        "moisturizer",
        "pm",
        "Evening hydration and barrier support"
      );
  };
// ============================================================
// PICK BEST AM TARGETED SERUM
// ============================================================

window.RISE_SKIN.pickMorningTargetedSerum =
  function (ranking) {

    const allowedFamilies = [
      "pigment_corrective",
      "acne_corrective",
      "corrective"
    ];

    const candidates =
      ranking.eligible
        .filter(
          (item) => {

            const family =
              window.RISE_SKIN
                .getProductFamily(
                  item.product
                );

            if (
              !allowedFamilies.includes(
                family
              )
            ) {
              return false;
            }

            const usage =
              window.RISE_SKIN.asArray(
                item.product?.amPm
              )
              .map(
                (value) =>
                  String(value).toUpperCase()
              );

            return usage.includes("AM");
          }
        )
        .sort(
          (a, b) =>
            b.score - a.score
        );

    return candidates[0] || null;
  };


// ============================================================
// ENSURE TARGETED SERUM EVERY MORNING
// COMPLETE + ELEVATED
// ============================================================

window.RISE_SKIN.ensureMorningTargetedSerum =
  function (
    routine,
    ranking
  ) {

    const existing =
      routine.am.find(
        (item) =>
          [
            "pigment_corrective",
            "acne_corrective",
            "corrective"
          ].includes(
            item.family
          )
      );

    if (existing) {
      return existing;
    }

    const item =
      window.RISE_SKIN
        .pickMorningTargetedSerum(
          ranking
        );

    if (!item) {
      return null;
    }

    const family =
      window.RISE_SKIN
        .getProductFamily(
          item.product
        );

    routine.am.push({
      product: item.product,
      score: item.score,
      family,
      timing: "am",
      reason:
        "Personalized targeted serum",
      providerReview:
        window.RISE_SKIN
          .requiresProviderReview(
            item.product
          )
    });

    return item;
  };


// ============================================================
// PICK BEST PRODUCT BY SPECIALTY MODULE
// ============================================================

window.RISE_SKIN.pickBestProductByModule =
  function (
    ranking,
    moduleName
  ) {

    const candidates =
      ranking.eligible
        .filter(
          (item) =>
            Array.isArray(
              item.product?.specialtyModules
            ) &&
            item.product
              .specialtyModules
              .includes(moduleName)
        )
        .sort(
          (a, b) =>
            b.score - a.score
        );

    return candidates[0] || null;
  };


// ============================================================
// ENSURE ELEVATED SPECIALTY PRODUCTS
// LIP + NECK
// ============================================================

window.RISE_SKIN.ensureElevatedSpecialties =
  function (
    routine,
    ranking
  ) {

    const neck =
      window.RISE_SKIN
        .pickBestProductByModule(
          ranking,
          "neck"
        );

    if (
      neck &&
      !window.RISE_SKIN
        .routineHasProduct(
          routine,
          neck.product
        )
    ) {
      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          neck,
          "neck",
          "Dedicated neck and décolleté support"
        );
    }

    const lip =
      window.RISE_SKIN
        .pickBestProductByModule(
          ranking,
          "lip"
        );

    if (
      lip &&
      !window.RISE_SKIN
        .routineHasProduct(
          routine,
          lip.product
        )
    ) {
      window.RISE_SKIN
        .addWithPreferredTiming(
          routine,
          lip,
          "lip",
          "Daily lip barrier and hydration support"
        );
    }
  };


// ============================================================
// IS RETINOID APPROPRIATE?
// ============================================================

window.RISE_SKIN.canUseRoutineRetinoid =
  function (profile) {

    if (profile.pregnant) {
      return false;
    }

    if (profile.prescriptionRetinoid) {
      return false;
    }

    if (
      profile.barrierStatus ===
      "impaired"
    ) {
      return false;
    }


    return true;
  };


// ============================================================
// FIND SAFETY SUBSTITUTE FOR RETINOID
// ============================================================

window.RISE_SKIN.pickRetinoidSubstitute =
  function (
    ranking,
    needs
  ) {

    const families = [];


    if (needs.pigment_corrective) {
      families.push(
        "pigment_corrective"
      );
    }


    if (needs.acne_corrective) {
      families.push(
        "acne_corrective"
      );
    }


    families.push(
      "corrective"
    );


    const candidates =
      families
        .map(
          (family) => ({
            family,
            item:
              window.RISE_SKIN
                .pickBestProduct(
                  ranking,
                  family
                )
          })
        )
        .filter(
          (candidate) =>
            candidate.item
        )
        .sort(
          (a, b) =>
            b.item.score -
            a.item.score
        );


    return (
      candidates[0] ||
      null
    );
  };


// ============================================================
// ENSURE RETINOID / SUBSTITUTE PM
// COMPLETE + ELEVATED
// ============================================================

window.RISE_SKIN.ensureEveningRetinoid =
  function (
    routine,
    ranking,
    needs
  ) {

    const profile =
      ranking.profile;


    if (
      window.RISE_SKIN
        .canUseRoutineRetinoid(
          profile
        )
    ) {

      const retinoid =
        window.RISE_SKIN
          .pickBestProduct(
            ranking,
            "retinoid"
          );


      if (retinoid) {

        // Remove competing strong PM actives.
        routine.pm =
          routine.pm.filter(
            (item) =>
              ![
                "retinoid",
                "exfoliant",
                "acne_corrective"
              ].includes(
                item.family
              )
          );


        routine.pm.push({
          product:
            retinoid.product,

          score:
            retinoid.score,

          family:
            "retinoid",

          timing:
            "pm",

          reason:
            "Core evening renewal step",

          providerReview: false
        });


        return;
      }
    }


    // --------------------------------------------------------
    // RETINOID NOT APPROPRIATE OR UNAVAILABLE
    // --------------------------------------------------------

    const substitute =
      window.RISE_SKIN
        .pickRetinoidSubstitute(
          ranking,
          needs
        );


    if (substitute) {

      window.RISE_SKIN
        .ensureRoutineProduct(
          routine,
          ranking,
          substitute.family,
          "pm",
          "Personalized evening corrective"
        );


      routine.notes.push(
        "Retinoid was not included based on your current skin/safety profile; an alternative corrective was selected."
      );

    } else {

      routine.notes.push(
        "Retinoid was not included based on your current skin/safety profile."
      );
    }
  };


// ============================================================
// REMOVE UNNECESSARY PREMIUM DUPLICATION
// ============================================================

window.RISE_SKIN.refinePremiumDuplication =
  function (routine) {

    const premiumFamilies = [
      "regenerative",
      "support"
    ];


    premiumFamilies.forEach(
      (family) => {

        const amItem =
          window.RISE_SKIN
            .findRoutineFamily(
              routine,
              "am",
              family
            );

        const pmItem =
          window.RISE_SKIN
            .findRoutineFamily(
              routine,
              "pm",
              family
            );


        // If the same premium product appears AM + PM,
        // preserve both when its own usage directions allow both.
        if (
          amItem &&
          pmItem &&
          window.RISE_SKIN
            .getProductId(
              amItem.product
            ) ===
          window.RISE_SKIN
            .getProductId(
              pmItem.product
            )
        ) {

          const usage =
            window.RISE_SKIN.asArray(
              amItem.product?.amPm
            )
            .map(
              (value) =>
                String(value).toUpperCase()
            );

          const intendedTwiceDaily =
            usage.includes("AM") &&
            usage.includes("PM");

          if (!intendedTwiceDaily) {
            window.RISE_SKIN
              .removeFamilyFromTiming(
                routine,
                "am",
                family
              );
          }
        }
      }
    );


    return routine;
  };


// ============================================================
// REFINE ESSENTIAL
// ============================================================

window.RISE_SKIN.refineEssentialRoutine =
  function (
    routine,
    ranking
  ) {

    window.RISE_SKIN
      .ensureDailyCleanser(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureDailySPF(
        routine,
        ranking
      );

    const hasRetinoid =
      routine.pm.some(
        (item) =>
          item.family === "retinoid"
      );

    if (hasRetinoid) {
      window.RISE_SKIN
        .ensureEveningMoisturizer(
          routine,
          ranking
        );
    }

    return window.RISE_SKIN
      .sortRoutine(
        routine
      );
  };


// ============================================================
// REFINE COMPLETE
// ============================================================

window.RISE_SKIN.refineCompleteRoutine =
  function (
    routine,
    ranking,
    needs
  ) {

    window.RISE_SKIN
      .ensureDailyCleanser(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureMorningAntioxidant(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureMorningTargetedSerum(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureDailySPF(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureEveningRetinoid(
        routine,
        ranking,
        needs
      );

    window.RISE_SKIN
      .ensureEveningMoisturizer(
        routine,
        ranking
      );

    return window.RISE_SKIN
      .sortRoutine(
        routine
      );
  };


// ============================================================
// REFINE ELEVATED
// ============================================================

window.RISE_SKIN.refineElevatedRoutine =
  function (
    routine,
    ranking,
    needs
  ) {

    // Same non-negotiable foundation as Complete.
    window.RISE_SKIN
      .ensureDailyCleanser(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureMorningAntioxidant(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureMorningTargetedSerum(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureDailySPF(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureEveningRetinoid(
        routine,
        ranking,
        needs
      );

    window.RISE_SKIN
      .ensureEveningMoisturizer(
        routine,
        ranking
      );

    window.RISE_SKIN
      .ensureElevatedSpecialties(
        routine,
        ranking
      );

    // Elevated does not mean duplicate everything.
    window.RISE_SKIN
      .refinePremiumDuplication(
        routine
      );

    return window.RISE_SKIN
      .sortRoutine(
        routine
      );
  };


// ============================================================
// OVERRIDE BUILD ROUTINES WITH REFINEMENT PASS
// ============================================================

window.RISE_SKIN.buildRoutines =
  function (profileInput) {

    const ranking =
      window.RISE_SKIN
        .rankProducts(
          profileInput
        );


    const needs =
      window.RISE_SKIN
        .getRoutineNeeds(
          ranking.profile,
          ranking.scores
        );


    // --------------------------------------------------------
    // BUILD ORIGINAL ROUTINES
    // --------------------------------------------------------

    let simple =
      window.RISE_SKIN
        .buildEssentialRoutine(
          ranking,
          needs
        );


    let complete =
      window.RISE_SKIN
        .buildCompleteRoutine(
          ranking,
          needs
        );


    let elevated =
      window.RISE_SKIN
        .buildElevatedRoutine(
          ranking,
          needs
        );


    // --------------------------------------------------------
    // APPLY ARCHITECTURAL RULES
    // --------------------------------------------------------

    simple =
      window.RISE_SKIN
        .refineEssentialRoutine(
          simple,
          ranking
        );


    complete =
      window.RISE_SKIN
        .refineCompleteRoutine(
          complete,
          ranking,
          needs
        );


    elevated =
      window.RISE_SKIN
        .refineElevatedRoutine(
          elevated,
          ranking,
          needs
        );


    // --------------------------------------------------------
    // PROVIDER GUIDANCE
    // --------------------------------------------------------

    const providerGuided =
      window.RISE_SKIN
        .getProviderGuidedMatches(
          ranking
        );


    simple.providerGuided =
      providerGuided;

    complete.providerGuided =
      providerGuided;

    elevated.providerGuided =
      providerGuided;


    // --------------------------------------------------------
    // RETURN
    // --------------------------------------------------------

    return {

      recommendedLevel:
        ranking.profile
          .routineCommitment ||
        "complete",

      profile:
        ranking.profile,

      scores:
        ranking.scores,

      needs,

      ranking,

      routines: {
        simple,
        complete,
        elevated
      },

      providerGuided
    };
  };


// ============================================================
// PART 2B VALIDATION
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Routine Architecture 2B loaded"
  );

}
/**
 * ============================================================
 * RISE SKIN CONCIERGE — PART 2C
 * Elevated Premium Layer Refinement
 * ============================================================
 */


// ============================================================
// REMOVE DUPLICATE PRODUCT ACROSS AM / PM
// ============================================================

window.RISE_SKIN.removeCrossTimingDuplicates =
  function (routine) {

    const amIds = new Set(
      routine.am.map(
        (item) =>
          window.RISE_SKIN
            .getProductId(item.product)
      )
    );


    // Keep duplicate AM / PM use ONLY when
// the product itself is intended for both.

routine.pm =
  routine.pm.filter(
    (item) => {

      const id =
        window.RISE_SKIN
          .getProductId(
            item.product
          );

      if (!amIds.has(id)) {
        return true;
      }

      const usage =
        window.RISE_SKIN.asArray(
          item.product?.amPm
        )
        .map(
          value =>
            String(value).toUpperCase()
        );

      const intendedTwiceDaily =
        usage.includes("AM") &&
        usage.includes("PM");

      return intendedTwiceDaily;
    }
  );


    return routine;
  };


// ============================================================
// LIMIT PREMIUM LAYERS
// ============================================================

window.RISE_SKIN.refineElevatedPremiumLayers =
  function (routine) {

    const regenerative =
      [
        ...routine.am,
        ...routine.pm
      ]
      .filter(
        (item) =>
          item.family ===
          "regenerative"
      );


    const support =
      [
        ...routine.am,
        ...routine.pm
      ]
      .filter(
        (item) =>
          item.family ===
          "support"
      );


    /*
     * If we already have a true regenerative
     * product, don't automatically stack a
     * generic support product just because
     * Elevated has room for one.
     */
    if (
      regenerative.length > 0 &&
      support.length > 0
    ) {

      const bestRegenerative =
        regenerative
          .slice()
          .sort(
            (a, b) =>
              b.score - a.score
          )[0];


      const bestSupport =
        support
          .slice()
          .sort(
            (a, b) =>
              b.score - a.score
          )[0];


      /*
       * Keep support only if it is actually
       * competitive with the regenerative
       * recommendation.
       *
       * Otherwise Elevated should stay elegant.
       */
      if (
        bestSupport.score <
        bestRegenerative.score
      ) {

        routine.am =
          routine.am.filter(
            (item) =>
              item.family !==
              "support"
          );


        routine.pm =
          routine.pm.filter(
            (item) =>
              item.family !==
              "support"
          );
      }
    }


    return routine;
  };


// ============================================================
// WRAP EXISTING ELEVATED REFINEMENT
// ============================================================

window.RISE_SKIN
  .refineElevatedRoutineBase =
    window.RISE_SKIN
      .refineElevatedRoutine;


window.RISE_SKIN.refineElevatedRoutine =
  function (
    routine,
    ranking,
    needs
  ) {

    routine =
      window.RISE_SKIN
        .refineElevatedRoutineBase(
          routine,
          ranking,
          needs
        );


    routine =
      window.RISE_SKIN
        .removeCrossTimingDuplicates(
          routine
        );


    routine =
      window.RISE_SKIN
        .refineElevatedPremiumLayers(
          routine
        );


    return window.RISE_SKIN
      .sortRoutine(
        routine
      );
  };


if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Elevated Refinement 2C loaded"
  );

}
console.log(
  "2C CHECK:",
  typeof window.RISE_SKIN.removeCrossTimingDuplicates,
  typeof window.RISE_SKIN.refineElevatedPremiumLayers,
  window.RISE_SKIN.refineElevatedRoutine.toString().includes(
    "removeCrossTimingDuplicates"
  )
);
/**
 * ============================================================
 * RISE SKIN CONCIERGE — FINAL ROUTINE CLEANUP
 * Guarantees final output obeys routine architecture
 * ============================================================
 */

// Save the current working routine builder.
window.RISE_SKIN.buildRoutinesBeforeFinalCleanup =
  window.RISE_SKIN.buildRoutines;


// ============================================================
// FINAL ELEVATED CLEANUP
// ============================================================

window.RISE_SKIN.finalizeElevatedRoutine =
  function (routine) {

    if (!routine) {
      return routine;
    }


    // --------------------------------------------------------
    // 1. REMOVE SAME PREMIUM PRODUCT FROM BOTH AM + PM
    // --------------------------------------------------------

    const amProductIds =
      new Set(
        routine.am.map(
          (item) =>
            window.RISE_SKIN
              .getProductId(item.product)
        )
      );


    routine.pm =
  routine.pm.filter(
    (item) => {

      const productId =
        window.RISE_SKIN
          .getProductId(
            item.product
          );

      if (
        !amProductIds.has(
          productId
        )
      ) {
        return true;
      }

      const usage =
        window.RISE_SKIN.asArray(
          item.product?.amPm
        )
        .map(
          value =>
            String(value).toUpperCase()
        );

      return (
        usage.includes("AM") &&
        usage.includes("PM")
      );
    }
  );


    // --------------------------------------------------------
    // 2. IF A TRUE REGENERATIVE PRODUCT EXISTS,
    //    REMOVE GENERIC SUPPORT UNLESS SUPPORT IS ESSENTIAL
    // --------------------------------------------------------

    const regenerativeItems =
      [
        ...routine.am,
        ...routine.pm
      ].filter(
        (item) =>
          item.family === "regenerative"
      );


    const supportItems =
      [
        ...routine.am,
        ...routine.pm
      ].filter(
        (item) =>
          item.family === "support"
      );


    if (
      regenerativeItems.length &&
      supportItems.length
    ) {

      const bestRegenerativeScore =
        Math.max(
          ...regenerativeItems.map(
            (item) => item.score
          )
        );


      const bestSupportScore =
        Math.max(
          ...supportItems.map(
            (item) => item.score
          )
        );


      if (
        bestRegenerativeScore >
        bestSupportScore
      ) {

        routine.am =
          routine.am.filter(
            (item) =>
              item.family !== "support"
          );


        routine.pm =
          routine.pm.filter(
            (item) =>
              item.family !== "support"
          );
      }
    }


    // --------------------------------------------------------
    // 3. GUARANTEE ONLY ONE REGENERATIVE PRODUCT
    // --------------------------------------------------------

    const allRegenerative =
      [
        ...routine.am.map(
          (item) => ({
            ...item,
            sourceTiming: "am"
          })
        ),

        ...routine.pm.map(
          (item) => ({
            ...item,
            sourceTiming: "pm"
          })
        )
      ]
      .filter(
        (item) =>
          item.family === "regenerative"
      )
      .sort(
        (a, b) =>
          b.score - a.score
      );


    if (allRegenerative.length > 1) {

      const winner =
        allRegenerative[0];


      routine.am =
        routine.am.filter(
          (item) =>
            item.family !== "regenerative"
        );


      routine.pm =
        routine.pm.filter(
          (item) =>
            item.family !== "regenerative"
        );


      routine[winner.sourceTiming].push({
        product: winner.product,
        score: winner.score,
        family: winner.family,
        timing: winner.sourceTiming,
        reason:
          "Advanced regenerative support",
        providerReview:
          winner.providerReview || false
      });
    }


    return window.RISE_SKIN
      .sortRoutine(routine);
  };


// ============================================================
// WRAP FINAL BUILD OUTPUT
// ============================================================

window.RISE_SKIN.buildRoutines =
  function (profileInput) {

    const result =
      window.RISE_SKIN
        .buildRoutinesBeforeFinalCleanup(
          profileInput
        );


    result.routines.elevated =
      window.RISE_SKIN
        .finalizeElevatedRoutine(
          result.routines.elevated
        );

        result.bodyRecommendations =
  window.RISE_SKIN
    .buildBodyRecommendations(
      profileInput
    );


    return result;
  };


// ============================================================
// DEBUG
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Final Routine Cleanup loaded"
  );

}

// ============================================================
// BODY RECOMMENDATION MODULE
// ============================================================

window.RISE_SKIN.buildBodyRecommendations =
  function (profileInput) {

    const profile =
      window.RISE_SKIN
        .normalizeProfileForEngine(
          profileInput
        );

    const concerns =
      window.RISE_SKIN.asArray(
        profile.bodyConcerns
      );

    if (
      !concerns.length ||
      concerns.includes("none")
    ) {
      return [];
    }

    const recommendations = [];

    const transform =
      window.RISE_SKIN.getProductById(
        "alastin-transform-body-treatment"
      );

    const alphaRetBody =
      window.RISE_SKIN.getProductById(
        "skinbetter-alpharet-body-overnight-cream"
      );

    const wantsTransform =
      concerns.includes("crepey_firmness") ||
      concerns.includes("stretch_marks") ||
      concerns.includes("prevention");

    if (wantsTransform && transform) {

      const gate =
        window.RISE_SKIN.evaluateProductGate(
          transform,
          profile
        );

      if (!gate.blocked) {

        recommendations.push({
          product: transform,
          type: "body-support",
          matchedConcerns:
            concerns.filter(
              (value) =>
                [
                  "crepey_firmness",
                  "stretch_marks",
                  "prevention"
                ].includes(value)
            ),
          reason:
            "Supports firmer-looking, smoother, healthier-looking body skin."
        });
      }
    }

    const wantsAlphaRetBody =
      concerns.includes("sun_damage") ||
      concerns.includes("body_breakouts") ||
      concerns.includes("stretch_marks");

    if (
      wantsAlphaRetBody &&
      alphaRetBody
    ) {

      const gate =
        window.RISE_SKIN.evaluateProductGate(
          alphaRetBody,
          profile
        );

      if (!gate.blocked) {

        recommendations.push({
          product: alphaRetBody,
          type: "body-corrective",
          matchedConcerns:
            concerns.filter(
              (value) =>
                [
                  "sun_damage",
                  "body_breakouts",
                  "stretch_marks"
                ].includes(value)
            ),
          reason:
            "Provides corrective body care for uneven tone, texture, and visible skin-quality concerns."
        });
      }
    }

    return recommendations;
  };